'use strict';

import ThreeGeo from '../../../libs/three-geo.esm';
import {
    Box3, Color,
    Group, LinearFilter, LinearMipmapLinearFilter, LinearSRGBColorSpace, MathUtils,
    MeshStandardMaterial,
    Raycaster,
    Vector3,
} from 'three';
import { projectMode, threeJsVariables } from '../../../settings';
import { OSMEnv } from '../../threejs/sceneBackground';
import { latLngAltToVector3 } from '../publicLayers/Layer';
import { OrbitDayAndNight } from '../../liveAnimations/DayNight/OrbitDayAndNight';
import { ColorCorrection } from '../../threejs/colorCorrection';
import { Environment } from '../Environment/Environment';
import { EnvironmentLayer } from '../Environment/EnvironmentLayers/EnvironmentLayer';
import { getShadowParams, prepareMaterial } from '../../threejs/autoProjectScript';
import { Loader } from '../../threejs/loader/Loader';
import { LUT } from '../../post-processing/Passes/LUT';
import { sceneObjectsGroup } from '../../points/points';
import { TilesTerrain } from './TilesTerrain';

let groundGroup = null;
let intersections = null;
let objectsToHeightIntersection = [];
const vector = new Vector3();
const terrainRaycaster = new Raycaster();
terrainRaycaster.firstHitOnly = true;
const originRay = new Vector3();
const directionRay = new Vector3();

export class Terrain {
    constructor () {
    }

    loadTerrain(group) {
        if (Environment.isActive()) {
            if (!Environment.fromBundle()) {
                TerrainEnvironment.scope?.create(group);
            }
            return;
        }

        if (!OrbitDayAndNight.isActive) return;

        const { ground, localization } = config.features.dayNight.settings;
        const mapBoxToken = 'pk.eyJ1IjoiYWJkdWxsYWJlc3QiLCJhIjoiY2tvc2RiZGlwMDBmdjJxbXc2cjRlZTRkcCJ9.-P0yGd4LAGQodEj87GKl-w';
        const lat = localization.lat;
        const lng = localization.lng;
        const type = ground.type || 'static';

        groundGroup = new Group();
        groundGroup.name = 'Terrain group';
        group.add(groundGroup);
        
        groundGroup.position.set(ground.position.x,ground.position.y,ground.position.z);
        groundGroup.rotation.set(ground.rotation.x, ground.rotation.y, ground.rotation.z);
        
        switch (type) {
            case 'static':
                const geo = new ThreeGeo({
                    tokenMapbox: mapBoxToken,
                    unitsSide: ground.unitsSide
                });
    
                let shadowSettings = getShadowParams(threeJsVariables.bundleConfig,'terrain');
                if (!shadowSettings) {
                    shadowSettings = {
                        castShadow: false,
                        receiveShadow: true
                    }
                }
                const terrain = geo.getTerrainRgb([lat,lng],ground.radius,ground.zoom);
                terrain.then(geoGroup => {
                    const box = new Box3();
                    const v = new Vector3();
                    const zero = new Vector3();
                    const anisotropy = threeJsVariables.renderer.capabilities.getMaxAnisotropy();
                    const colors = new ColorCorrection().getColors('ground');
        
                    geoGroup.traverse(el => {
                        if (el.isMesh) {
                            const t = el.material.map;
                            t.magFilter = LinearFilter;
                            t.minFilter = LinearMipmapLinearFilter;
                            t.anisotropy = anisotropy;
                            threeJsVariables.renderer.initTexture(t);
                            box.setFromObject(el).getCenter(v);
                            el.geometry.center();
                            el.position.copy(v);
    
                            el.material = new MeshStandardMaterial({
                                color: new Color().setStyle(colors.color,LinearSRGBColorSpace),
                                emissive: new Color().setStyle(colors.emissive,LinearSRGBColorSpace),
                                map: t,
                                envMap: OSMEnv.preloaded.envMap,
                                transparent: true
                            });
    
                            el.material.onBeforeCompile = (shader) => {
        
                                shader.uniforms.groundDensity = {value: ground.fogDensity};
        
                                const vertexVarying = 'varying float zFOG;\n';
                                const vertex_position = shader.vertexShader.replace(
                                    '#include <project_vertex>',
                                    [
                                        '#include <project_vertex>',
                                        'zFOG = -gl_Position.z;'
                                    ].join( '\n' )
                                );
        
                                shader.vertexShader = vertexVarying + vertex_position;
        
                                const fragmentVarying = 'varying float zFOG;\n uniform float groundDensity;\n';
                                const fragmentMap = shader.fragmentShader.replace(
                                    '#include <opaque_fragment>',
                                    [
                                        '#include <opaque_fragment>',
                                        `gl_FragColor.a = smoothstep(0.5,1.0,exp( -groundDensity * groundDensity * zFOG * zFOG ));`
                                    ].join( '\n' )
                                );
        
                                shader.fragmentShader = fragmentVarying + fragmentMap;
                            };
                            
                            const distance = el.position.distanceTo(zero);
                            // el.receiveShadow = !(ground.unshadowDistance && ground.unshadowDistance > distance);
                            el.receiveShadow = ground.unshadowDistance ? !(ground.unshadowDistance > distance) : shadowSettings.receiveShadow;
                            el.castShadow = shadowSettings.castShadow;
                        }
                    });
        
                    groundGroup.add(geoGroup);
                });
                break;
            case 'tiles':
                const tiles = new TilesTerrain(mapBoxToken,lat,lng);
                const map = tiles.load('MeshStandardMaterial');
                const scale = Math.cos(MathUtils.degToRad(lat));
                groundGroup.scale.set(scale,scale,scale);
                groundGroup.add(map);
                
                break;
        }
    }

    intersectHeightFromLatLng(lat,lng) {
        if (groundGroup === null) return 0;

        vector.copy(latLngAltToVector3(
            {
                lat: Number(lat),
                lng: Number(lng)
            }
            ,{
                lat: Number(config.localization.latitude),
                lng: Number(config.localization.longitude)
            }
        ));

        originRay.set(vector.x, 2000, vector.z);
        directionRay.set(vector.x, -2000, vector.z);
        directionRay.sub(originRay).normalize();
        terrainRaycaster.set(originRay, directionRay);

        intersections = terrainRaycaster.intersectObjects(groundGroup.children);
        if (intersections.length) {
            return intersections[0].point.y;
        } else {
            return 0;
        }
    }

    intersectHeightFromVector3(vec3) {
        objectsToHeightIntersection = [];
        if (groundGroup && OSMEnv.osm && OSMEnv.osm.parent) {
            objectsToHeightIntersection.push(groundGroup);
        }

        if (sceneObjectsGroup.children.length) {
            objectsToHeightIntersection.push(sceneObjectsGroup);
        }

        if (threeJsVariables.tilesRenderer) {
            objectsToHeightIntersection.push(threeJsVariables.scene.getObjectByName('async env'));
        }

        if (objectsToHeightIntersection.length === 0) return 0;

        vector.set(vec3.x,vec3.y,vec3.z);
        originRay.set(vector.x, 2000, vector.z);
        directionRay.set(vector.x, -2000, vector.z);
        directionRay.sub(originRay).normalize();
        terrainRaycaster.set(originRay, directionRay);

        intersections = terrainRaycaster.intersectObjects(objectsToHeightIntersection);
        if (intersections.length) {
            return intersections[0].point.y;
        } else {
            return 0;
        }
    }

    static applyColorCorrection(colorKey,color) {
        if (groundGroup === null) return;

        groundGroup.traverse(el => {
            if (el.isMesh) {
                el.material[colorKey].setStyle(color,LinearSRGBColorSpace);
            }
        })
    }

}

export class TerrainEnvironment extends EnvironmentLayer{
    static scope = null;

    constructor () {
        super('Terrain');
        this.lat = 0;
        this.lng = 0;
        this.material = null;
        this.zoom = 14;
        this.radius = 4000;
        this.fog = 0;
        this.unitsSide = 5650;

        this.group.rotation.x = - Math.PI / 2;
        TerrainEnvironment.scope = this;
    }

    create(parentGroup) {
        const mapBoxToken = 'pk.eyJ1IjoiYWJkdWxsYWJlc3QiLCJhIjoiY2tvc2RiZGlwMDBmdjJxbXc2cjRlZTRkcCJ9.-P0yGd4LAGQodEj87GKl-w';
        const { ground } = config.features.dayNight.settings;
        const type = ground.type || 'static';
        
        if (parentGroup) {
            groundGroup = new Group();
            groundGroup.name = 'Terrain';
            groundGroup.rotation.x = - Math.PI / 2;
            groundGroup.position.copy(this.group.position);
            const matrix = Environment.getEnvironmentTransformation();
            groundGroup.applyMatrix4(matrix);
            parentGroup.add(groundGroup);
        } else {
            groundGroup = this.group;
        }
        
        let shadowSettings = getShadowParams(threeJsVariables.bundleConfig,'terrain');
        if (!shadowSettings) {
            shadowSettings = {
                castShadow: false,
                receiveShadow: true
            }
        }
        
        switch (type) {
            case 'static':
                const geoEnv = new ThreeGeo({
                    tokenMapbox: mapBoxToken,
                    unitsSide: this.unitsSide
                });
    
                const terrainEnv = geoEnv.getTerrainRgb([this.lat,this.lng],this.radius,this.zoom);
                terrainEnv.then(geoGroup => {
                    const box = new Box3();
                    const v = new Vector3();
                    const anisotropy = threeJsVariables.renderer.capabilities.getMaxAnisotropy();
                    const lut = LUT.getScope('terrain');
                    geoGroup.traverse(el => {
                        if (el.isMesh) {
                            const t = el.material.map;
                            t.magFilter = LinearFilter;
                            t.minFilter = LinearMipmapLinearFilter;
                            t.anisotropy = anisotropy;
                            threeJsVariables.renderer.initTexture(t);
                            box.setFromObject(el).getCenter(v);
                            el.geometry.center();
                            el.position.copy(v);
                
                            const material = this.material.clone();
                            material.map = t;
                            el.material = material;
                            el.castShadow = shadowSettings.castShadow;
                            el.receiveShadow = shadowSettings.receiveShadow;
                
                            if (lut) {
                                lut.include(material);
                            }
                            else {
                                material.onBeforeCompile = (shader) => {
                                    this.upgradeShader(shader);
                                };
                            }
                        }
                    });
                    groundGroup.add(geoGroup);
                });
                break;
            case 'tiles':
                groundGroup.rotation.x = 0;
                const tiles = new TilesTerrain(mapBoxToken,this.lat,this.lng);
                const map = tiles.load(this.material.type);
                const scale = Math.cos(MathUtils.degToRad(this.lat));
                groundGroup.scale.set(scale,scale,scale);
                groundGroup.add(map);
                break;
        }
    }
    
    loadFromBundle (scene, conf) {
        const config = conf[this.id];
        if (!config || projectMode.AirMode.active) return;
        this.settings = config;
        this.create();
    }
    
    show() {
        this.group.visible = true;
    }

    hide() {
        this.group.visible = false;
    }

    set settings(data) {
        this.lat = data.lat;
        this.lng = data.lng;
        this.group.position.fromArray(data.position);
        this.zoom = data.zoom;
        this.radius = data.radius;
        this.fog = data.fog;
        if (data.unitsSide) {
            this.unitsSide = data.unitsSide;
        }
        const loader = new Loader();
        const textureLoader = loader.textureLoader;
        this.material = prepareMaterial(data.material,textureLoader,data.materialType);
    }

    upgradeShader(shader) {
        if (Environment.fromBundle()) return;
        shader.uniforms.groundDensity = {value: this.fog};

        const vertexVarying = 'varying float zFOG;\n';
        const vertex_position = shader.vertexShader.replace(
            '#include <project_vertex>',
            [
                '#include <project_vertex>',
                'zFOG = -gl_Position.z;'
            ].join( '\n' )
        );

        shader.vertexShader = vertexVarying + vertex_position;

        const fragmentVarying = 'varying float zFOG;\n uniform float groundDensity;\n';
        const fragmentMap = shader.fragmentShader.replace(
            '#include <opaque_fragment>',
            [
                '#include <opaque_fragment>',
                `gl_FragColor.a = smoothstep(0.5,1.0,exp( -groundDensity * groundDensity * zFOG * zFOG ));`
            ].join( '\n' )
        );

        shader.fragmentShader = fragmentVarying + fragmentMap;
    }

    static upgradeShader(shader) {
        TerrainEnvironment.scope?.upgradeShader(shader);
    }
}
